import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  staffList: [], // GET /staff-rating/company/staff — ADMIN leaderboard
  companyRatings: [], // GET /staff-rating/company/ratings — ADMIN flat list of every rating
  companyAverage: { average: 0, count: 0 }, // GET /staff-rating/company/average — ADMIN/MANAGER
  employeeRatings: [], // GET /staff-rating/:employeeId — raw ratings for the selected employee
  employeeMonthly: [], // GET /staff-rating/:employeeId/monthly — 12-month trend
  loading: false,
  employeeLoading: false,
  isSuccess: false,
  errors: []
};

export const fetchCompanyStaffRatings = createAsyncThunk(
  'staffRating/fetchCompanyStaff',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/staff-rating/company/staff');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchCompanyRatings = createAsyncThunk(
  'staffRating/fetchCompanyRatings',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/staff-rating/company/ratings');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchCompanyRatingAverage = createAsyncThunk(
  'staffRating/fetchCompanyAverage',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/staff-rating/company/average');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchEmployeeRatings = createAsyncThunk(
  'staffRating/fetchEmployeeRatings',
  async (employeeId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/staff-rating/${employeeId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchEmployeeMonthlyRatings = createAsyncThunk(
  'staffRating/fetchEmployeeMonthly',
  async (employeeId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/staff-rating/${employeeId}/monthly`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const staffRatingSlice = createSlice({
  name: 'staffRating',
  initialState,
  reducers: {
    clearSelectedEmployeeRatings: (state) => {
      state.employeeRatings = [];
      state.employeeMonthly = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyStaffRatings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyStaffRatings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.staffList = payload || [];
      })
      .addCase(fetchCompanyStaffRatings.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      })

      .addCase(fetchCompanyRatings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyRatings.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.companyRatings = payload || [];
      })
      .addCase(fetchCompanyRatings.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      })

      .addCase(fetchCompanyRatingAverage.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyRatingAverage.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.companyAverage = payload || { average: 0, count: 0 };
      })
      .addCase(fetchCompanyRatingAverage.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      })

      .addCase(fetchEmployeeRatings.pending, (state) => {
        state.employeeLoading = true;
      })
      .addCase(fetchEmployeeRatings.fulfilled, (state, { payload }) => {
        state.employeeLoading = false;
        state.employeeRatings = payload || [];
      })
      .addCase(fetchEmployeeRatings.rejected, (state) => {
        state.employeeLoading = false;
      })

      .addCase(fetchEmployeeMonthlyRatings.pending, (state) => {
        state.employeeLoading = true;
      })
      .addCase(fetchEmployeeMonthlyRatings.fulfilled, (state, { payload }) => {
        state.employeeLoading = false;
        state.employeeMonthly = payload || [];
      })
      .addCase(fetchEmployeeMonthlyRatings.rejected, (state) => {
        state.employeeLoading = false;
      });
  }
});

export const { clearSelectedEmployeeRatings } = staffRatingSlice.actions;

export default staffRatingSlice.reducer;
